import { useMemo, useState } from 'react';
import { formatNameWithCode } from '~/utils';
import {
  contactLabel,
  type Contact,
  type RecipientOrganization,
} from '~/utils/contact';
import type { RecipientGroup } from '~/pages/buy-lists/sections/RequestForQuoteSummaryPanels';

export type SellerSelection<TOrg extends RecipientOrganization> = {
  organization: TOrg;
  contacts: Contact[];
};

type SellerInvitation = {
  sellingOrganizationId: string;
  recipientUserIds: string[];
};

// Each pick keeps its own organization, so a contact stays in the request after a search
// or the next page takes that organization off screen.
type SelectedOrganization<TOrg> = {
  organization: TOrg;
  contacts: Map<string, Contact>;
};

type Selection<TOrg> = Map<string, SelectedOrganization<TOrg>>;

function mergeInto<TOrg extends RecipientOrganization>(
  selection: Selection<TOrg>,
  organization: TOrg,
  contacts: Contact[]
): void {
  const picked = selection.get(organization.id);
  const merged = new Map(picked?.contacts);
  contacts.forEach((contact) => merged.set(contact.id, contact));
  selection.set(organization.id, {
    organization: picked?.organization ?? organization,
    contacts: merged,
  });
}

type RecipientsState<TOrg> = {
  selection: Selection<TOrg>;
  turnedOnSellerLists: ReadonlyMap<string, ReadonlySet<string>>;
};

function withContacts<TOrg extends RecipientOrganization>(
  state: RecipientsState<TOrg>,
  selections: SellerSelection<TOrg>[]
): RecipientsState<TOrg> {
  const selection = new Map(state.selection);
  selections.forEach(({ organization, contacts }) =>
    mergeInto(selection, organization, contacts)
  );
  return { ...state, selection };
}

function withoutOrganizations<TOrg extends RecipientOrganization>(
  state: RecipientsState<TOrg>,
  organizationIds: string[]
): RecipientsState<TOrg> {
  const selection = new Map(state.selection);
  organizationIds.forEach((organizationId) => selection.delete(organizationId));
  const turnedOnSellerLists = new Map(
    [...state.turnedOnSellerLists].filter(
      ([, sellerIds]) => !organizationIds.some((id) => sellerIds.has(id))
    )
  );
  return { selection, turnedOnSellerLists };
}

export function useSellerRecipients<
  TOrg extends RecipientOrganization = RecipientOrganization,
>() {
  const [state, setState] = useState<RecipientsState<TOrg>>(() => ({
    selection: new Map(),
    turnedOnSellerLists: new Map(),
  }));
  const { selection, turnedOnSellerLists } = state;

  const toggleContact = (organization: TOrg, contact: Contact) => {
    setState((previous) => {
      const picked = previous.selection.get(organization.id);
      if (!picked?.contacts.has(contact.id)) {
        return withContacts(previous, [{ organization, contacts: [contact] }]);
      }
      const contacts = new Map(picked.contacts);
      contacts.delete(contact.id);
      return {
        ...previous,
        selection: new Map(previous.selection).set(organization.id, {
          ...picked,
          contacts,
        }),
      };
    });
  };

  const toggleOrganization = (organization: TOrg) => {
    setState((previous) =>
      previous.selection.has(organization.id)
        ? withoutOrganizations(previous, [organization.id])
        : withContacts(previous, [{ organization, contacts: [] }])
    );
  };

  const addContacts = (selections: SellerSelection<TOrg>[]) => {
    setState((previous) => withContacts(previous, selections));
  };

  const turnOnSellerList = (
    sellerListId: string,
    selections: SellerSelection<TOrg>[]
  ) => {
    setState((previous) => {
      const next = withContacts(previous, selections);
      return {
        ...next,
        turnedOnSellerLists: new Map(next.turnedOnSellerLists).set(
          sellerListId,
          new Set(selections.map(({ organization }) => organization.id))
        ),
      };
    });
  };

  const turnOffSellerList = (sellerListId: string) => {
    setState((previous) => {
      const turnedOn = new Map(previous.turnedOnSellerLists);
      const listSellerIds = turnedOn.get(sellerListId) ?? new Set<string>();
      turnedOn.delete(sellerListId);
      const keptIds = new Set(
        [...turnedOn.values()].flatMap((sellerIds) => [...sellerIds])
      );
      return withoutOrganizations(
        { ...previous, turnedOnSellerLists: turnedOn },
        [...listSellerIds].filter((id) => !keptIds.has(id))
      );
    });
  };

  const isSellerListOn = (sellerListId: string) =>
    turnedOnSellerLists.has(sellerListId);

  const selectedContactIds = useMemo(
    () =>
      new Set(
        [...selection.values()].flatMap(({ contacts }) => [...contacts.keys()])
      ),
    [selection]
  );

  const selectedOrganizations = useMemo(
    () =>
      [...selection.values()].map(({ organization, contacts }) => ({
        organization,
        contacts: [...contacts.values()],
      })),
    [selection]
  );

  const selectedOrganizationIds = useMemo(
    () => new Set(selection.keys()),
    [selection]
  );

  const isOrganizationSelected = (organizationId: string) =>
    selection.has(organizationId);

  const sellerInvitations: SellerInvitation[] = useMemo(
    () =>
      selectedOrganizations.map(({ organization, contacts }) => ({
        sellingOrganizationId: organization.id,
        recipientUserIds: contacts.map((contact) => contact.id),
      })),
    [selectedOrganizations]
  );

  const recipientGroups: RecipientGroup[] = useMemo(
    () =>
      selectedOrganizations.map(({ organization, contacts }) => ({
        organizationId: organization.id,
        organizationLabel: formatNameWithCode(
          organization.name,
          organization.sellingOrganizationCode
        ),
        contacts: contacts.map((contact) => ({
          id: contact.id,
          name: contactLabel(contact),
        })),
      })),
    [selectedOrganizations]
  );

  return {
    selectedContactIds,
    selectedOrganizationIds,
    isOrganizationSelected,
    toggleContact,
    toggleOrganization,
    addContacts,
    isSellerListOn,
    turnOnSellerList,
    turnOffSellerList,
    selectedOrganizations,
    sellerInvitations,
    recipientGroups,
  };
}

export type SellerRecipients<
  TOrg extends RecipientOrganization = RecipientOrganization,
> = ReturnType<typeof useSellerRecipients<TOrg>>;
